/**
 * Race the promise against an abort signal.
 *
 * @param promise - The promise to race.
 * @param signal - The abort signal.
 * @returns The promise.
 */
export function abortable<T>(promise: Promise<T>, signal?: AbortSignal) {
  return Promise.race(signal ? [promise, aborted(signal)] : [promise]);
}

AbortSignal.prototype.throwIfAborted ??= function throwIfAborted(
  this: AbortSignal
) {
  if (this.aborted) {
    throw new DOMException(
      this.reason ?? 'This operation was aborted.',
      'AbortError'
    );
  }
};

/**
 * Create a promise that rejects when the abort signal is aborted.
 *
 * @param signal - The abort signal.
 * @returns The promise. Never resolves.
 */
export function aborted(signal: AbortSignal): Promise<never> {
  const existing = signalPromises.get(signal);
  if (existing) {
    return existing;
  }
  const promise = new Promise<never>((__, reject) => {
    const onAbort = () => {
      signalPromises.delete(signal);
      try {
        signal.throwIfAborted();
      } catch (error) {
        reject(error);
      }
    };
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
  signalPromises.set(signal, promise);
  return promise;
}
const signalPromises = new WeakMap<AbortSignal, Promise<never>>();
